import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import JsonLd from "@/components/JsonLd";
import { getCityPageBySlug } from "@/data/citiesContent";
import { getCityTemplate } from "@/data/cityTemplates";

const SITE_URL = "https://cartonmove.fr";

// Helper: returns the city name with correct French preposition.
// "Le Plessis-Robinson" -> "au Plessis-Robinson"
// "Les Lilas" -> "aux Lilas"
// "La Garenne-Colombes" -> "à La Garenne-Colombes" (feminine, no elision)
// Default: "à <name>"
const aCity = (name) => {
  if (name.startsWith("Le ")) return `au ${name.slice(3)}`;
  if (name.startsWith("Les ")) return `aux ${name.slice(4)}`;
  return `à ${name}`;
};

const FEATURED_SLUGS = [
  "carton-standard-renforce",
  "carton-a-livres",
  "carton-12-bouteilles",
  "carton-75-verres",
];

const buildCityServiceSchema = (city) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Vente de cartons et fournitures de déménagement",
  name: `Cartons de déménagement à ${city.name}`,
  description: `Vente en ligne de cartons et fournitures de déménagement livrés à ${city.name} (${city.deptCode}, ${city.deptName}). Propre flotte de livraison en Île-de-France.`,
  provider: {
    "@type": "Organization",
    name: "cartonmove",
    url: SITE_URL,
  },
  areaServed: [
    {
      "@type": "City",
      name: city.name,
      containedInPlace: {
        "@type": "AdministrativeArea",
        name: `${city.deptName} (${city.deptCode})`,
      },
    },
  ],
  url: `${SITE_URL}/carton-demenagement/${city.slug}`,
});

/**
 * Calcule le H1 spécifique à chaque template ville. Slug → titre.
 * Sert aussi de table de référence pour les nouvelles villes :
 * il suffit d'y ajouter une ligne lors de la création d'un template.
 */
const buildH1 = (city) => {
  const titles = {
    "neuilly-sur-seine": `Cartons de déménagement à ${city.name}`,
    nanterre: `Cartons de déménagement à ${city.name} : particuliers et professionnels`,
    montrouge: `Cartons de déménagement à ${city.name} : particuliers et entreprises`,
    sevres: `Cartons de déménagement à ${city.name} : l'assurance d'un départ réussi`,
    "levallois-perret": `Cartons de déménagement et outils d'emballage à ${city.name}`,
    clichy: `Vos fournitures de déménagement professionnelles à ${city.name}`,
    courbevoie: `Fournitures de déménagement à ${city.name} : mettez votre patrimoine à l'abri`,
    "la-garenne-colombes": `Carton de déménagement à ${city.name} : matériel pro au meilleur prix`,
    colombes: `Cartons de déménagement à ${city.name} : qualité haut de gamme`,
    gennevilliers: `Vos cartons de déménagement à ${city.name} : maîtrisez le choc du transit`,
    "villeneuve-la-garenne": `Vente et livraison de cartons de déménagement à ${city.name}`,
    antony: `Cartons de déménagement à ${city.name} : pour particuliers et entreprises`,
    "asnieres-sur-seine": `Cartons de déménagement à ${city.name} de qualité professionnelle`,
    bagneux: `Tout le matériel de déménagement à ${city.name}`,
    "bois-colombes": `Votre boutique de cartons de déménagement à ${city.name}`,
    "bourg-la-reine": `Vos cartons de déménagement sur mesure à ${city.name}`,
    "chatenay-malabry": `Cartons de déménagement et emballages professionnels à ${city.name}`,
    puteaux: `Vos cartons de déménagement à ${city.name} : vente et livraison`,
    "rueil-malmaison": `Vos fournitures de déménagement à ${city.name}`,
    suresnes: `Fournitures de déménagement à ${city.name} : emballages et protections`,
    garches: `Cartons de déménagement pour particuliers et entreprises à ${city.name}`,
    "saint-cloud": `Vos fournitures de déménagement sur mesure à ${city.name}`,
    vaucresson: `Fournitures de déménagement et d'emballage à ${city.name}`,
    malakoff: `Cartons de déménagement pour particuliers et professionnels à ${city.name}`,
    chatillon: `Fourniture de déménagement premium à ${city.name} : objectif zéro casse`,
    clamart: `Cartons de déménagement à ${city.name} — anticipez et sécurisez`,
    "fontenay-aux-roses": `Vos cartons de déménagement à ${city.name} : qualité et prix`,
    "le-plessis-robinson": `Cartons de déménagement au Plessis-Robinson : matériel pro et éco-économique`,
    sceaux: `Cartons de déménagement à ${city.name} : vente et livraison`,
    meudon: `Vente de cartons de déménagement à ${city.name} : particuliers et professionnels`,
    chaville: `Cartons de déménagement haute qualité à ${city.name}`,
    "ville-davray": `Cartons de déménagement à ${city.name} : qualité haut de gamme`,
    "issy-les-moulineaux": `Cartons de déménagement à ${city.name} : fournitures haut de gamme`,
    "marnes-la-coquette": `Cartons de déménagement à ${city.name} : emballages et protections`,
    vanves: `Cartons de déménagement à ${city.name} : pour entreprises et particuliers`,
  };
  return titles[city.templateId] || `Fournitures de déménagement à ${city.name}`;
};

/**
 * Paragraphe d'intro spécifique à chaque template ville.
 * Une JSX par templateId pour préserver les apostrophes/typographie françaises.
 */
const Intro = ({ city }) => {
  switch (city.templateId) {
    case "neuilly-sur-seine":
      return (
        <>
          Chaque déménagement mérite une organisation efficace et des
          fournitures adaptées. À {city.name}, notre boutique spécialisée met
          à votre disposition des cartons de déménagement robustes et des
          accessoires d&apos;emballage pensés pour simplifier chacune des
          étapes de votre projet.
        </>
      );
    case "nanterre":
      return (
        <>
          Vous pilotez un projet de mobilité résidentielle ou le transfert de
          vos locaux professionnels à {city.name}&nbsp;? Dans une ville en
          pleine effervescence économique, la clé d&apos;un déménagement
          fluide réside dans l&apos;efficacité de vos protections. Notre
          enseigne en ligne supprime les approximations en vous fournissant
          des structures de conditionnement haut de gamme. Conçus pour
          dissiper les chocs et sécuriser vos transferts, nos produits vous
          permettent de préparer vos colis avec méthode, protégeant vos
          biens les plus précieux comme vos dossiers les plus stratégiques.
        </>
      );
    case "montrouge":
      return (
        <>
          Vous orchestrez un changement de domicile ou le transfert de votre
          entité professionnelle à {city.name}&nbsp;? Dans le tissu urbain
          dense de la petite couronne, chaque étape de votre logistique doit
          être optimisée. Notre commerce en ligne fluidifie vos démarches en
          mettant à votre disposition des structures d&apos;emballage de
          haute technicité. Finies les boîtes de fortune récupérées à la
          hâte ; nos modules en carton compact offrent une protection
          structurelle maximale pour préserver l&apos;intégrité de vos
          dossiers, de votre mobilier et de vos objets personnels.
        </>
      );
    case "sevres":
      return (
        <>
          Un déménagement réussi commence par des fournitures adaptées. À
          {" "}{city.name}, notre boutique en ligne vous accompagne avec une
          sélection complète de cartons de déménagement, protections et
          accessoires conçus pour simplifier l&apos;emballage de vos
          affaires.
        </>
      );
    case "levallois-perret":
      return (
        <>
          Vous orchestrez un projet de déménagement ou un transfert de
          bureaux à {city.name}&nbsp;? Dans une ville où chaque minute
          compte, l&apos;improvisation n&apos;a pas sa place. La réussite
          de votre transition dépend directement de la résistance de vos
          contenants. Notre enseigne spécialisée balaie les emballages
          standards pour vous fournir des structures de protection à forte
          densité de compactage. Conçus pour dissiper les impacts, soutenir
          de lourdes charges et s&apos;empiler au millimètre dans les
          véhicules, nos produits sécurisent vos biens de bout en bout.
        </>
      );
    case "clichy":
      return (
        <>
          Vous orchestrez votre futur emménagement ou départ de
          {" "}{city.name}&nbsp;? Pour transporter vos biens sans encombre à
          travers les Hauts-de-Seine, notre boutique en ligne vous fournit
          des solutions d&apos;emballage de premier choix. Fini le stress
          des cartons de récupération qui se déchirent ou s&apos;affaissent
          au moindre mouvement. Nous mettons à votre disposition des
          équipements rigides et fiables, spécialement étudiés pour
          préserver vos vêtements, livres et objets de valeur du premier
          coup de scotch jusqu&apos;au déballage.
        </>
      );
    case "courbevoie":
      return (
        <>
          Vous orchestrez la transition de votre mobilier à
          {" "}{city.name}&nbsp;? Face aux défis techniques d&apos;un
          transfert urbain, la qualité de vos contenants est votre
          meilleure assurance. Notre plateforme bouscule le marché
          traditionnel en vous donnant accès à des armatures de carton à
          haute densité. Finis les rabats qui lâchent ou les boîtes qui
          s&apos;affaissent sous le poids des charges. Nous fournissons
          aux foyers et aux entreprises un matériel de calibre technique,
          conçu pour sanctuariser vos livres, vêtements et objets précieux
          tout au long de leur manipulation.
        </>
      );
    case "la-garenne-colombes":
      return (
        <>
          Vous cherchez des cartons de déménagement pas chers et solides à
          {" "}{city.name}&nbsp;? Pour éviter la casse et réussir votre
          déménagement, le choix de vos cartons est capital. Notre boutique
          en ligne vous propose une large gamme de fournitures de qualité
          professionnelle. Que vous soyez un particulier ou une entreprise,
          achetez tout votre matériel d&apos;emballage au même endroit pour
          préparer vos cartons en toute tranquillité.
        </>
      );
    case "colombes":
      return (
        <>
          Organiser un déménagement à {city.name} demande des équipements
          adaptés pour emballer et transporter vos biens en toute sécurité.
          C&apos;est pourquoi nous vous proposons une sélection de cartons
          résistants et d&apos;accessoires d&apos;emballage conçus pour
          protéger efficacement vos affaires durant chaque étape du
          déménagement. Que vous déménagiez un appartement ou une maison,
          nos fournitures vous permettent de préparer vos cartons avec
          sérénité et de bénéficier d&apos;une protection optimale pour
          l&apos;ensemble de vos effets personnels.
        </>
      );
    case "gennevilliers":
      return (
        <>
          Vous quittez les rives de la Seine où vous implantez votre
          structure à {city.name}&nbsp;? Face à la réalité mécanique
          d&apos;un déménagement, le choix de vos contenants décide du sort
          de votre patrimoine. Notre plateforme rejette les emballages
          jetables et fins pour vous équiper d&apos;armoires en carton
          compressé de haute technologie. Capables de neutraliser la
          compression et de survivre aux secousses des trajets urbains, nos
          produits créent une barrière inviolable autour de vos archives,
          écrans et objets personnels.
        </>
      );
    case "villeneuve-la-garenne":
      return (
        <>
          En bord de Seine, l&apos;organisation d&apos;une transition
          résidentielle ou le déploiement d&apos;une nouvelle infrastructure
          professionnelle à {city.name} ne tolère aucune approximation
          logistique. Notre plateforme numérique rompt avec les standards
          fragiles du commerce grand public en vous proposant des
          architectures d&apos;emballage à haute densité. Conçus pour
          supporter les contraintes des plateformes de chargement et
          sécuriser vos biens face aux vibrations routières, nos modules
          vous permettent de rationaliser vos préparatifs avec une
          efficacité maximale.
        </>
      );
    case "antony":
      return (
        <>
          Vous préparez votre déménagement à {city.name} ou dans les
          Hauts-de-Seine&nbsp;? Pour emballer vos biens en toute sécurité,
          notre boutique en ligne vous propose une large sélection de
          cartons professionnels et de matériel de protection de haute
          qualité. Spécialement conçus pour résister au transport, nos
          produits s&apos;adaptent à tous vos besoins, des livres aux
          objets les plus fragiles.
        </>
      );
    case "asnieres-sur-seine":
      return (
        <>
          Vous organisez votre déménagement à {city.name} ou dans les
          environs&nbsp;? Pour emballer tous vos biens en toute sécurité,
          notre boutique en ligne met à votre disposition une large gamme
          de cartons et de matériel de protection haute résistance. Pensés
          pour les exigences des déménagements urbains, nos produits
          protègent efficacement vos livres, vêtements et objets fragiles.
          Commandez en quelques clics et bénéficiez de notre service
          logistique pour une livraison directement à votre domicile à
          Asnières, vous permettant de lancer vos préparatifs en toute
          sérénité.
        </>
      );
    case "bagneux":
      return (
        <>
          Vous préparez votre déménagement à {city.name} ou dans les
          communes limitrophes des Hauts-de-Seine&nbsp;? Pour emballer
          l&apos;ensemble de vos biens en toute sécurité, notre boutique
          en ligne vous propose une large gamme de cartons et de matériel
          de protection haute résistance. Conçus pour répondre aux
          exigences des professionnels, nos produits sécurisent vos
          livres, vêtements et objets de valeur. Commandez vos fournitures
          en quelques clics et lancez vos préparatifs en toute sérénité.
        </>
      );
    case "bois-colombes":
      return (
        <>
          Vous organisez prochainement un déménagement à {city.name}&nbsp;?
          Facilitez la préparation de vos affaires grâce à notre large
          gamme de cartons de déménagement et d&apos;accessoires
          d&apos;emballage. Nous mettons à votre disposition des
          fournitures robustes et fiables, adaptées aussi bien aux objets
          du quotidien qu&apos;aux biens les plus délicats.
        </>
      );
    case "bourg-la-reine":
      return (
        <>
          Marre des cartons de récupération qui craquent au pire
          moment&nbsp;? Pour votre projet à {city.name}, passez à la
          vitesse supérieure. Notre e-boutique met à votre disposition un
          équipement de niveau professionnel pour sécuriser l&apos;ensemble
          de vos biens. Fini le stress des objets brisés : nous fournissons
          des emballages ultra-robustes conçus pour encaisser les
          manipulations sans broncher. Commandez aujourd&apos;hui et
          préparez vos affaires avec l&apos;assurance d&apos;un résultat
          impeccable.
        </>
      );
    case "chatenay-malabry":
      return (
        <>
          Prêt pour votre grand départ de {city.name}&nbsp;? Ne laissez pas
          la sécurité de vos affaires au hasard avec des cartons fragiles
          ou inadaptés. Notre e-boutique vous donne accès à du matériel de
          qualité industrielle, conçu pour encaisser les chocs, supporter
          l&apos;empilement et protéger vos objets précieux du premier au
          dernier kilomètre. Prenez le contrôle de vos préparatifs dès
          aujourd&apos;hui avec des fournitures fiables.
        </>
      );
    case "puteaux":
      return (
        <>
          Vous pilotez un changement de locaux professionnels ou une
          mutation résidentielle à {city.name}&nbsp;? À l&apos;ombre des
          infrastructures majeures et dans le sillage de l&apos;activité
          économique du secteur, votre logistique personnelle exige une
          efficacité absolue. Notre point de vente virtuel élimine les
          points de friction en vous fournissant des blindages cartonnés
          de haute technicité. Finies les boîtes fragiles qui menacent de
          céder : nos structures composites intègrent un haut niveau de
          compactage pour garantir un transit fluide et une protection
          inviolable de votre patrimoine informatique, de vos dossiers et
          de vos objets personnels.
        </>
      );
    case "rueil-malmaison":
      return (
        <>
          Vous planifiez votre installation ou le déploiement de vos
          bureaux à {city.name}&nbsp;? Pour aborder cette transition avec
          un esprit parfaitement serein, la qualité de vos préparatifs est
          votre meilleur atout. Notre boutique en ligne transforme votre
          approche du carton en vous donnant accès à des structures de
          protection haut de gamme. Conçus pour simplifier le tri,
          résister aux manutentions énergiques et s&apos;empiler avec une
          stabilité parfaite, nos produits protègent vos souvenirs de
          famille comme vos dossiers essentiels contre tous les aléas du
          transport.
        </>
      );
    case "suresnes":
      return (
        <>
          Face au Mont-Valérien, l&apos;organisation d&apos;une transition
          résidentielle ou d&apos;un transfert de bureaux à {city.name}
          {" "}requiert un matériel à la hauteur de vos exigences. Notre
          plateforme digitale rompt avec le carton ordinaire en déployant
          des architectures d&apos;emballage hautement résistantes. Conçus
          pour faire face aux contraintes de la manutention moderne, nos
          produits éliminent le risque de rupture ou d&apos;affaissement.
          En vous équipant chez nous, vous structurez vos préparatifs
          pièce par pièce, transformant la phase critique de la mise en
          boîte en une procédure fluide, sécurisée et parfaitement
          maîtrisée.
        </>
      );
    case "garches":
      return (
        <>
          Vous pilotez un projet de transfert résidentiel ou professionnel
          à {city.name}&nbsp;? Face aux contraintes physiques d&apos;un
          déménagement, la structure de vos contenants dicte la sécurité
          de vos biens. Notre plateforme en ligne réinvente l&apos;accès
          aux fournitures logistiques en mettant à votre disposition des
          armatures techniques à haute densité. Conçus pour neutraliser
          les pressions, encaisser les secousses du transport et
          s&apos;empiler sans fléchir, nos modules isolent vos objets
          précieux dans une enveloppe hautement protectrice. Prenez une
          longueur d&apos;avance sur la logistique.
        </>
      );
    case "saint-cloud":
      return (
        <>
          Vous préparez un déménagement à {city.name}&nbsp;? Pour emballer
          et transporter vos affaires dans les meilleures conditions,
          notre boutique en ligne vous propose une sélection de cartons de
          déménagement et de fournitures d&apos;emballage adaptés à tous
          les besoins. Pratiques, résistants et faciles à utiliser, nos
          produits vous permettent d&apos;organiser efficacement chaque
          étape de votre déménagement. Que vous changiez de logement ou
          que vous prépariez un transfert de bureaux, vous trouverez tout
          le matériel nécessaire pour protéger vos biens et faciliter leur
          transport. Commandez simplement en ligne et recevez des
          fournitures de qualité pour déménager en toute confiance.
        </>
      );
    case "vaucresson":
      return (
        <>
          Vous initiez un transfert de résidence ou la relocalisation de
          vos bureaux à {city.name}&nbsp;? Dans cette commune verdoyante
          aux propriétés et demeures exigeantes, préserver l&apos;intégrité
          de vos biens et de vos œuvres de valeur requiert un équipement
          irréprochable. Notre plateforme en ligne met fin aux
          approximations logistiques en vous fournissant des modules de
          protection de calibre industriel. Conçus pour neutraliser les
          vibrations du transport, résister aux torsions et supporter des
          empilements massifs, nos cartons vous assurent une préparation
          méthodique et un transfert totalement sécurisé.
        </>
      );
    case "malakoff":
      return (
        <>
          Vous préparez un déménagement ou le transfert de vos locaux à
          {" "}{city.name}&nbsp;? Pour aborder ce changement de vie en
          toute décontraction, l&apos;organisation est votre meilleure
          arme. Notre plateforme en ligne réinvente vos préparatifs en
          vous donnant accès à du matériel d&apos;emballage de qualité
          supérieure. Finis les cartons de récupération qui lâchent sous
          le poids de vos affaires&nbsp;; nos structures renforcées sont
          spécialement conçues pour encaisser les contraintes du transport
          et mettre votre patrimoine à l&apos;abri des aléas.
        </>
      );
    case "chatillon":
      return (
        <>
          Vous quittez {city.name}&nbsp;? Ne confiez pas vos souvenirs et
          vos objets de valeur à des cartons fragiles ou de récupération.
          Notre boutique en ligne bouscule les codes en vous fournissant
          du matériel de calibre industriel. Fini les fonds qui cèdent et
          les piles qui s&apos;effondrent dans le camion. Prenez les
          devants avec des emballages robustes conçus pour encaisser la
          réalité du terrain et sécuriser chaque pièce de votre logement.
        </>
      );
    case "clamart":
      return (
        <>
          Vous changez d&apos;adresse à {city.name} ou dans le sud des
          Hauts-de-Seine&nbsp;? Pour mettre à l&apos;abri vos effets
          personnels, notre plateforme en ligne vous propose des structures
          d&apos;emballage haut de gamme. Pas question de transiger sur la
          sécurité&nbsp;: nous mettons entre vos mains un matériel robuste
          qui évite les déformations et protège vos souvenirs contre les
          imprévus du transport. Équipez-vous dès aujourd&apos;hui pour
          aborder vos cartons avec méthode.
        </>
      );
    case "fontenay-aux-roses":
      return (
        <>
          Vous quittez bientôt {city.name} ou vous venez de vous y
          installer&nbsp;? Pour réussir la transition de vos effets
          personnels, la robustesse de vos contenants est primordiale.
          Notre boutique en ligne rompt avec les standards fragiles de la
          grande distribution en vous proposant du matériel de transport
          de niveau industriel. Conçus pour résister aux contraintes du
          terrain, nos produits sécurisent vos souvenirs et simplifient
          chaque étape de votre mise en carton.
        </>
      );
    case "le-plessis-robinson":
      return (
        <>
          Vous préparez un changement de domicile ou un transfert de
          bureaux au Plessis-Robinson&nbsp;? Pour que votre projet se
          déroule sans accroc, la solidité de vos emballages est votre
          meilleure alliée. Notre boutique en ligne met à votre
          disposition une gamme complète de cartons de déménagement
          professionnels et de fournitures de haute qualité. Conçus pour
          résister à la charge et s&apos;empiler parfaitement dans les
          camions, nos produits vous permettent d&apos;emballer tout votre
          patrimoine en toute sérénité.
        </>
      );
    case "sceaux":
      return (
        <>
          Préparer un déménagement à {city.name} nécessite des fournitures
          fiables pour emballer et protéger efficacement l&apos;ensemble
          de vos biens. Notre boutique en ligne vous propose une gamme
          complète de cartons de déménagement et d&apos;accessoires
          conçus pour faciliter le rangement, l&apos;organisation et le
          transport de vos affaires. Grâce à leur conception robuste, nos
          cartons conviennent aussi bien aux objets du quotidien
          qu&apos;aux biens plus fragiles. Que vous déménagiez un
          appartement, une maison ou des locaux professionnels, vous
          bénéficiez de solutions pratiques et résistantes pour préparer
          votre projet en toute sérénité.
        </>
      );
    case "meudon":
      return (
        <>
          Vous organisez votre changement d&apos;adresse ou le transfert
          de vos bureaux à {city.name}&nbsp;? Dans cette commune
          vallonnée aux accès parfois exigeants, la réussite de votre
          projet commence par une préparation méthodique. Notre boutique
          en ligne supprime l&apos;improvisation en vous fournissant du
          matériel d&apos;emballage de qualité supérieure. Conçus pour
          absorber les vibrations du transport et résister à la
          déformation, nos cartons sécurisent vos biens de bout en bout,
          transformant la corvée de la mise en boîte en une procédure
          simple et ordonnée.
        </>
      );
    case "chaville":
      return (
        <>
          Votre départ de {city.name} approche à grands pas&nbsp;? Inutile
          de vider votre portefeuille pour sécuriser vos affaires. Notre
          plateforme réinvente l&apos;accès aux fournitures de
          déménagement en combinant performance industrielle et tarifs
          ultra-compétitifs. Nous armons les particuliers et les
          entreprises avec un équipement robuste, capable d&apos;affronter
          le transport et de préserver l&apos;intégrité de vos biens, du
          chargement jusqu&apos;au déballage.
        </>
      );
    case "ville-davray":
      return (
        <>
          Vous préparez un déménagement résidentiel ou le transfert de
          vos bureaux à {city.name}&nbsp;? Pour sécuriser vos biens dans
          cette commune verdoyante aux accès parfois escarpés, la qualité
          de vos fournitures est déterminante. Notre boutique en ligne
          élimine les risques de casse en vous proposant des cartons de
          calibre industriel. Conçus pour résister aux manutentions
          physiques et aux contraintes du transport, nos produits vous
          garantissent une mise en boîte méthodique, sereine et
          parfaitement structurée.
        </>
      );
    case "issy-les-moulineaux":
      return (
        <>
          Vous pilotez le transfert de votre entreprise ou de votre
          résidence à {city.name}&nbsp;? Dans une ville dynamique où tout
          s&apos;accélère, la réussite de votre projet repose sur la
          solidité de vos bases. Notre plateforme en ligne rompt avec
          l&apos;approximation en mettant à votre disposition des
          structures de protection de calibre technologique. Conçus pour
          isoler vos biens des agressions extérieures et absorber
          l&apos;énergie des impacts, nos modules transforment l&apos;étape
          redoutée de l&apos;emballage en une procédure fluide, carrée et
          totalement sécurisée.
        </>
      );
    case "marnes-la-coquette":
      return (
        <>
          Vous planifiez le transfert de votre résidence ou de votre
          structure professionnelle à {city.name}&nbsp;? Pour aborder ce
          changement de cap avec un maximum de clarté, l&apos;organisation
          de vos préparatifs est essentielle. Notre plateforme spécialisée
          rompt avec les fournitures ordinaires en vous proposant des
          armatures de carton compressé de haute densité. Conçus pour
          dissiper les vibrations de la route et supporter des charges
          importantes, nos produits créent un bouclier fiable autour de
          votre patrimoine mobilier, de vos archives et de vos objets les
          plus précieux.
        </>
      );
    case "vanves":
      return (
        <>
          Vous planifiez votre prochain emménagement ou la
          restructuration de vos espaces professionnels à
          {" "}{city.name}&nbsp;? Pour réussir cette transition en toute
          décontraction, la qualité de vos enveloppes de transport est
          primordiale. Notre boutique en ligne supprime l&apos;improvisation
          en mettant à votre disposition des cartons de déménagement de
          haute technicité architecturale. Conçus pour résister au gerbage
          intensif et amortir les aléas des trajets urbains, nos produits
          sécurisent l&apos;intégralité de vos collections, de vos
          vêtements et de vos équipements technologiques.
        </>
      );
    default:
      return (
        <>
          Préparez votre déménagement à {city.name} avec des cartons
          robustes et des accessoires d&apos;emballage adaptés à tous vos
          besoins. Notre boutique en ligne vous propose des fournitures de
          qualité pour protéger efficacement vos biens durant le transport.
          Commandez facilement vos cartons et recevez rapidement tout le
          matériel nécessaire pour organiser votre déménagement en toute
          tranquillité.
        </>
      );
  }
};

/**
 * Petit paragraphe d'introduction complémentaire (uniquement Neuilly + Sèvres).
 */
const ExtraIntro = ({ city }) => {
  if (city.templateId === "neuilly-sur-seine") {
    return (
      <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
        Que vous ayez quelques effets personnels à transporter ou un
        logement entier à emballer, nos solutions vous permettent de
        protéger vos biens, d&apos;optimiser le rangement de vos cartons
        et de faciliter leur transport. Commandez en ligne et bénéficiez
        d&apos;un matériel de qualité pour préparer votre déménagement en
        toute sérénité.
      </p>
    );
  }
  if (city.templateId === "sevres") {
    return (
      <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
        Que vous prépariez le déménagement d&apos;un studio, d&apos;une
        maison familiale ou de bureaux professionnels, nos solutions vous
        aident à organiser efficacement vos biens et à les protéger durant
        le transport. Grâce à des cartons résistants et faciles à
        manipuler, vous gagnez en confort, en sécurité et en efficacité à
        chaque étape de votre projet.
      </p>
    );
  }
  if (city.templateId === "bois-colombes") {
    return (
      <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
        Cartons standards, cartons renforcés, protections, adhésifs et
        matériel de calage : tout est pensé pour vous aider à emballer
        efficacement et à transporter vos effets personnels dans les
        meilleures conditions. Commandez en ligne en toute simplicité et
        recevez rapidement les équipements nécessaires à la réussite de
        votre déménagement.
      </p>
    );
  }
  return null;
};

const CityPage = () => {
  const { citySlug } = useParams();
  const city = getCityPageBySlug(citySlug);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchProducts()
      .then((res) => setProducts(res || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useDocumentTitle(
    city
      ? `Carton déménagement ${city.name} (${city.deptCode}) — Livraison rapide | cartonmove`
      : "Ville introuvable — cartonmove",
    city
      ? `Cartons et fournitures de déménagement livrés rapidement à ${city.name}. Service local, propre flotte, accessoires de protection et de fermeture.`
      : undefined
  );

  if (!city) return <Navigate to="/cartons-demenagement" replace />;

  const template = getCityTemplate(city.templateId);
  const featured = FEATURED_SLUGS
    .map((s) => products.find((p) => p.slug === s))
    .filter(Boolean);

  return (
    <div data-testid="city-page" data-city-slug={city.slug}>
      <JsonLd id="ld-city-service" data={buildCityServiceSchema(city)} />
      {/* Breadcrumb back to department */}
      <div className="container-cdm pt-5">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 text-sm text-[#5b6b7a] hover:text-[#0f1d2b]"
          data-testid="city-back-to-dept"
        >
          <ArrowRight className="h-4 w-4 rotate-180" /> Retour à
          {" "}{city.deptName} ({city.deptCode})
        </Link>
      </div>

      {/* ======= SECTION 1 — Hero + 4 produits ======= */}
      <section className="pt-10 pb-16 md:pt-14 md:pb-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EEF3F7] border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
              <MapPin className="h-3.5 w-3.5" strokeWidth={2.6} />
              {city.name} · {city.deptName} ({city.deptCode})
            </div>
            <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px] text-[#0f1d2b] leading-tight">
              {buildH1(city)}
            </h1>
            <p className="mt-3 text-[14px] text-[#5b6b7a]">
              Livraison rapide {aCity(city.name)} et dans tout le {city.deptCode}
              {" "}avec notre propre flotte.
            </p>
          </div>

          {/* 4 featured products */}
          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div
              className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
              data-testid="city-featured-products"
            >
              {featured.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
              ))}
            </div>
          )}

          <p className="mt-10 text-[15px] md:text-base text-[#3a4856] leading-relaxed max-w-4xl">
            <Intro city={city} />
          </p>
          <ExtraIntro city={city} />

          <Link
            to="/cartons-demenagement"
            className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors [-webkit-tap-highlight-color:transparent]"
            data-testid="city-cta-shop-top"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </section>

      {/* Sections SEO spécifiques au template */}
      {template?.Sections && <template.Sections city={city} products={products} />}

      {/* Big Déménagement partenaire — uniquement sur les pages phares
          (sur les satellites, le CTA est inséré au milieu directement
          dans le template) */}
      {["neuilly-sur-seine", "boulogne-billancourt", "nanterre", "montrouge", "sevres"].includes(city.slug) && (
        <section className="py-12 bg-white">
          <div className="container-cdm">
            <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta`} />
          </div>
        </section>
      )}
    </div>
  );
};

export default CityPage;
